/**
 * Utilitários compartilhados entre os relatórios
 */
import React from 'react';
import { Chip, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subDays } from 'date-fns';

export const fmtInt = (n: number) => (n ?? 0).toLocaleString('pt-BR');

/** Converte string YYYY-MM-DD para Date local */
export function parseYmdToLocalDate(str: string): Date | null {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, (m || 1) - 1, d || 1);
}

/** Converte Date para string YYYY-MM-DD */
export function dateToYmd(d: Date | null): string {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Retorna o primeiro e último dia do mês atual em formato Date */
export function getCurrentMonthRange(): { startDate: Date; endDate: Date } {
  const now = new Date();
  return {
    startDate: startOfMonth(now),
    endDate: endOfMonth(now)
  };
}

/** Retorna o primeiro e último dia do mês atual em formato YYYY-MM-DD */
export function getFirstAndLastDayOfMonth(): { firstDay: string; lastDay: string } {
  const range = getCurrentMonthRange();
  return {
    firstDay: dateToYmd(range.startDate),
    lastDay: dateToYmd(range.endDate),
  };
}

/** Opções de filtros rápidos reutilizáveis */
export const quickFilterOptions = [
  {
    label: 'Hoje',
    value: 'today',
    getRange: () => ({
      startDate: new Date(),
      endDate: new Date()
    })
  },
  {
    label: 'Mês Atual',
    value: 'current-month',
    getRange: () => {
      const r = getCurrentMonthRange();
      return { startDate: r.startDate, endDate: r.endDate };
    }
  },
  {
    label: 'Últimos 30 dias',
    value: 'last-30',
    getRange: () => ({
      startDate: subDays(new Date(), 30),
      endDate: new Date()
    })
  },
  {
    label: 'Últimos 3 meses',
    value: 'last-3-months',
    getRange: () => ({
      startDate: subMonths(new Date(), 3),
      endDate: new Date()
    })
  },
  {
    label: 'Ano Atual',
    value: 'current-year',
    getRange: () => ({
      startDate: startOfYear(new Date()),
      endDate: endOfYear(new Date())
    })
  },
  {
    label: 'Todos os Períodos',
    value: 'all',
    getRange: () => ({
      startDate: null,
      endDate: null
    })
  }
];

export function useRelatorioCores() {
  const theme = useTheme();
  return {
    ativo: theme.palette.success.main,
    bloqueado: theme.palette.warning.main,
    cancelado: theme.palette.error.main,
    aguardando: theme.palette.info.main,
    primario: theme.palette.primary.main,
    secundario: theme.palette.secondary.main,
  };
}

const statusMap: Record<string, { label: string }> = {
  ativo: { label: 'Ativo' },
  inativo: { label: 'Inativo' },
  bloqueado: { label: 'Bloqueado' },
  cancelado: { label: 'Cancelado' },
  aguardando_instalacao: { label: 'Ag. Instalação' },
  aberto: { label: 'Aberto' },
  pago: { label: 'Pago' },
  vencido: { label: 'Vencido' },
  parcialmente_pago: { label: 'Parc. Pago' },
  suspenso: { label: 'Suspenso' },
  inadimplente: { label: 'Inadimplente' },
};

export function getStatusChip(status: string, cores: ReturnType<typeof useRelatorioCores>) {
  const colorMap: Record<string, string> = {
    ativo: cores.ativo,
    inativo: '#9e9e9e',
    bloqueado: cores.bloqueado,
    cancelado: cores.cancelado,
    aguardando_instalacao: cores.aguardando,
    aberto: cores.aguardando,
    pago: cores.ativo,
    vencido: cores.cancelado,
    parcialmente_pago: cores.bloqueado,
    suspenso: cores.bloqueado,
    inadimplente: cores.cancelado,
  };
  const m = statusMap[status] || { label: status };
  const color = colorMap[status] || '#9e9e9e';
  return (
    <Chip
      label={m.label}
      size="small"
      sx={{
        bgcolor: alpha(color, 0.12),
        color,
        fontWeight: 700,
        fontSize: '0.68rem',
        height: 22,
        border: `1px solid ${alpha(color, 0.3)}`,
      }}
    />
  );
}
